import {
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
} from '@mui/material';
import { Field, Form, FormikProvider, useFormik } from 'formik';
import { RadioGroup, TextField } from 'formik-mui';
import { find, map, sortBy } from 'lodash';
import React, { Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import {
  FormControls,
  FormikCheckbox,
  FormikSelect,
  HelpTooltip,
  usePostSavePrompt,
  useUnsavedChangesGuard,
} from 'components';
import { SCI_CUSTOMER_ALTERNATIVES_VALIDATION_SCHEMA } from 'constants/shops.constants';
import { CustomerDataCollectionOrder, PayinOrderTimeoutConfigKey } from 'enums';
import { useMeta, useUser, useUserContext } from 'hooks';
import { TranslationNamespace } from 'i18n';
import {
  SciCustomerAlternativesConfig as SciCustomerAlternativesConfigType,
  ShopPaymentTypeConfig,
} from 'types';
import { getSciDomainOptions, shopUtils } from 'utils';

import { CustomerFieldsPayin } from '../../CustomerFieldsPayin';
import { DeepLinkPaymentPageSetting } from '../../DeepLinkPaymentPageSetting';
import { SciCustomerAlternativesConfig } from '../../SciCustomerAlternativesConfig';
import { SciPaymentTypeConfigs } from '../../SciPaymentTypeConfigs';
import {
  useManageShop,
  ManageShopSection,
  ManageShopTabActions,
} from '../components';
import { useShopSubmit } from '../hooks';

const DEFAULT_SCI_CUSTOMER_ALTERNATIVES_CONFIG = {
  alternativeAmountEnabled: false,
  alternativeTradeMethodEnabled: false,
};

type SciValues = {
  sciDomainId: string;
  displayName: string;
  useDeepLinkPaymentPage: boolean;
  includeDeeplinkInResponse: boolean;
  sciPaymentConfigs: any[];
  configs: any[];
  collectCustomerReceipts: boolean;
  customerDataCollectionOrder: CustomerDataCollectionOrder;
  sciCustomTradeMethodsEnabled: boolean;
  sciCustomerAlternativesConfig?: SciCustomerAlternativesConfigType;
  clarityEnabled: boolean;
};

export const SciTab: React.FC = () => {
  const { shopId, shopData } = useManageShop();
  const { onSavedSuccess } = usePostSavePrompt();
  const { isSciCustomerAlternativesEnabled, isClarityEnabled } = useMeta();
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.shops.manage',
  });
  const { t: tCommon } = useTranslation();
  const { isAdminOrTechOperator } = useUser();
  const { sciDomains, paymentTypes } = useUserContext();
  const sciDomainOptions = useMemo(
    () => getSciDomainOptions(sciDomains),
    [sciDomains],
  );

  const { handleSubmit: submitShop } = useShopSubmit({
    shopId,
    onSuccess: onSavedSuccess,
  });

  const normalizeConfigs = useCallback(
    (configs: ShopPaymentTypeConfig[]) =>
      map(configs, (config) => {
        const customerFields = find(paymentTypes, {
          id: config.paymentTypeId,
        })?.customerFields;
        config.customerFields = sortBy(
          map(
            customerFields,
            (field) =>
              find(config.customerFields, { name: field.name }) || {
                name: field.name,
                hidden: field.hidden,
              },
          ),
          'hidden',
        );
        return config;
      }),
    [paymentTypes],
  );

  const initialValues: SciValues = useMemo(() => {
    if (shopData) {
      return {
        sciDomainId: shopData.sciDomainId || '',
        displayName: shopData.displayName || '',
        useDeepLinkPaymentPage: shopData.useDeepLinkPaymentPage || false,
        includeDeeplinkInResponse: shopData.includeDeeplinkInResponse || false,
        sciPaymentConfigs: shopData.sciPaymentConfigs || [],
        configs: normalizeConfigs(shopData.configs || []),
        collectCustomerReceipts: shopData.collectCustomerReceipts || false,
        customerDataCollectionOrder:
          shopData.customerDataCollectionOrder ||
          CustomerDataCollectionOrder.BeforePayment,
        sciCustomTradeMethodsEnabled:
          shopData.sciCustomTradeMethodsEnabled || false,
        sciCustomerAlternativesConfig:
          shopData.sciCustomerAlternativesConfig ||
          DEFAULT_SCI_CUSTOMER_ALTERNATIVES_CONFIG,
        clarityEnabled: shopData.clarityEnabled || false,
      };
    }
    return {
      sciDomainId: '',
      displayName: '',
      useDeepLinkPaymentPage: false,
      includeDeeplinkInResponse: false,
      sciPaymentConfigs: [],
      configs: [],
      collectCustomerReceipts: false,
      customerDataCollectionOrder: CustomerDataCollectionOrder.BeforePayment,
      sciCustomTradeMethodsEnabled: false,
      sciCustomerAlternativesConfig: DEFAULT_SCI_CUSTOMER_ALTERNATIVES_CONFIG,
      clarityEnabled: false,
    };
  }, [shopData, normalizeConfigs]);

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        sciDomainId: Yup.string().nullable().optional(),
        displayName: Yup.string().nullable().optional(),
        useDeepLinkPaymentPage: Yup.boolean().nullable(),
        includeDeeplinkInResponse: Yup.boolean().nullable(),
        sciPaymentConfigs: Yup.array().of(
          Yup.object().shape({
            paymentTypeId: Yup.string().required(tCommon('errors.required')),
            bankId: Yup.string().optional().nullable(),
            cascadeId: Yup.string().optional().nullable(),
          }),
        ),
        sciCustomerAlternativesConfig:
          SCI_CUSTOMER_ALTERNATIVES_VALIDATION_SCHEMA,
      }),
    [tCommon],
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    validateOnMount: true,
    onSubmit: submitShop,
  });

  useUnsavedChangesGuard(formik);

  return (
    <FormikProvider value={formik}>
      <Form>
        {isAdminOrTechOperator && (
          <ManageShopSection title={t('sections.sci_configuration.title')}>
            <FormControls>
              <FormikSelect
                label={t('fields.sci_domain')}
                name="sciDomainId"
                options={sciDomainOptions}
                noneOption
              />
              <Field
                component={TextField}
                label={t('fields.display_name')}
                name="displayName"
                size="small"
                fullWidth
              />
              <DeepLinkPaymentPageSetting />
              {isClarityEnabled && (
                <FormControl>
                  <FormikCheckbox
                    name="clarityEnabled"
                    label={t('fields.clarity_enabled')}
                    helperText={t('fields.clarity_enabled_helper')}
                  />
                </FormControl>
              )}
              <Divider>{t('fields.sci_payment_types')}</Divider>
              <FormControl>
                <FormikCheckbox
                  name="sciCustomTradeMethodsEnabled"
                  label={t('fields.custom_trade_methods_enabled')}
                />
              </FormControl>
              <SciPaymentTypeConfigs />
            </FormControls>
          </ManageShopSection>
        )}

        <ManageShopSection
          title={
            <Fragment>
              {t('sections.customer_fields_payin.title')}
              <HelpTooltip
                title={t('sections.customer_fields_payin.description')}
              />
            </Fragment>
          }
        >
          <FormControls>
            <CustomerFieldsPayin
              paymentTypes={paymentTypes}
              hideTitle
              disabled={!isAdminOrTechOperator}
            />
            <Divider />
            <FormControl>
              <FormikCheckbox
                name="collectCustomerReceipts"
                label={t('fields.collect_customer_receipts')}
                disabled={!isAdminOrTechOperator}
              />
            </FormControl>
            {isAdminOrTechOperator && (
              <FormControl>
                <FormLabel>
                  {t('fields.customer_data_collection_order')}
                </FormLabel>
                <Field
                  component={RadioGroup}
                  name="customerDataCollectionOrder"
                  row
                >
                  {Object.values(CustomerDataCollectionOrder).map((order) => (
                    <FormControlLabel
                      key={order}
                      value={order}
                      control={<Radio />}
                      label={shopUtils.getCustomerDataCollectionOrder(order)}
                    />
                  ))}
                </Field>
              </FormControl>
            )}
          </FormControls>
        </ManageShopSection>

        {isSciCustomerAlternativesEnabled && isAdminOrTechOperator && (
          <ManageShopSection
            title={t('sections.sci_customer_alternatives.title')}
          >
            <SciCustomerAlternativesConfig
              payinTimeoutConfigRequisites={
                shopData?.payinTimeoutConfig?.[
                  PayinOrderTimeoutConfigKey.Requisites
                ]
              }
            />
          </ManageShopSection>
        )}

        <ManageShopTabActions readOnly={!isAdminOrTechOperator} />
      </Form>
    </FormikProvider>
  );
};
